import React, { useContext, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { trimObject } from "utils/utils";
import { queryPayment, queryTransaction } from "./model";
import { PortalContext } from "App";
import Spin from "components/Spin/Spin";
import s from "./OfflinePaymentsDetails.module.scss";
import useTransactionActionDialogs from "../components/transaction-dialog-hook/useTransactionActionDialogs";
import PaymentDetailSection from "../components/payment-detail-section/PaymentDetailSection";
import TransactionDetailSection from "../components/transaction-detail-section/TransactionDetailSection";

const PAGE_LOCALE_KEY = "page.transactions.offlinePaymentDetails.";

const OfflinePaymentsDetails = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;

  const [isPaymentLoading, setIsPaymentLoading] = useState(true);
  const [isTransactionLoading, setIsTransactionLoading] = useState(true);
  const isLoading = isPaymentLoading || isTransactionLoading;

  const [paymentId, setPaymentId] = useState(null);
  const [datasource, setDatasource] = useState({});

  const {
    TransactionActionDialogComponents,
    openReceiptDialog,
    openVoidDialog,
    openRefundDialog,
  } = useTransactionActionDialogs({
    env: "offline",
    datasource,
    handleRefetch: () => {
      initialFetchPaymentDetail();
      initialFetchTransactionDetail();
    },
  });

  useLayoutEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setPaymentId(detailPageState[path].id);
    } else if (sessionState && sessionState[path]) {
      setPaymentId(sessionState[path].id);
    } else {
      if (paymentId == "") setPaymentId("close");
    }
  }, [detailPageState[path]]);

  useLayoutEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    if (paymentId == "close") {
      redirectFn("close", { tabId: path });
    }

    if (!paymentId) {
      //message "unknown payment id" will be shown
      setIsPaymentLoading(false);
      setIsTransactionLoading(false);
      return;
    }
    fetchDetails(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, [paymentId]);

  const fetchDetails = (getIsOudated) => {
    initialFetchPaymentDetail(getIsOudated);
    initialFetchTransactionDetail(getIsOudated);
  };

  const initialFetchPaymentDetail = async (getIsOudated) => {
    setIsPaymentLoading(true);
    const apiResponse = await queryPayment(
      trimObject({
        id: paymentId,
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setDatasource((state) => ({ ...state, payment: responseBody }));
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "paymentDetails.fetchFail"), "error");
          }
        }
      }
    }

    setIsPaymentLoading(false);
  };

  const initialFetchTransactionDetail = async (getIsOudated) => {
    setIsTransactionLoading(true);
    const apiResponse = await queryTransaction(
      trimObject({
        payment_id: paymentId,
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setDatasource((state) => ({ ...state, transaction: responseBody }));
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "transactionDetails.fetchFail"),
              "error",
            );
          }
        }
      }
    }

    setIsTransactionLoading(false);
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  return (
    <div className={s["container"]}>
      <div className={s["content-container"]}>
        <div className={isLoading ? s["loading-now"] : ""}>
          {isLoading ? (
            <Spin />
          ) : (
            <div className={s["details-container"]}>
              {paymentId && (
                <PaymentDetailSection
                  offline
                  payment={datasource?.payment?.data[0]}
                />
              )}
              {paymentId && (
                <TransactionDetailSection
                  offline
                  transactions={datasource?.transaction?.data}
                  payment={datasource?.payment?.data[0]}
                  openSendReceiptDialog={openReceiptDialog}
                  openVoidTransactionDialog={openVoidDialog}
                  openRefundTransactionDialog={openRefundDialog}
                />
              )}
              {paymentId && TransactionActionDialogComponents}
              {!paymentId && (
                <span>{t(PAGE_LOCALE_KEY + "error.noPaymentId")}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(OfflinePaymentsDetails);
